/*@jsxRuntime automatic @jsxImportSource react*/
import {jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {MainLayout} from '~/layouts/main';
import {Figure} from '~/components/Figure';
import {Quiz} from '~/components/Quiz';
export const meta = {
  title: 'OCP | Введение',
  description: 'Введение в принцип открытости-закрытости, его смысл, основные понятия'
};
const MDXLayout = ({children}) => _jsx(MainLayout, {
  meta: meta,
  children: children
});
function _createMdxContent(props) {
  const _components = Object.assign({
    section: "section",
    h1: "h1",
    p: "p",
    h2: "h2",
    a: "a",
    ul: "ul",
    li: "li",
    em: "em",
    code: "code",
    h3: "h3",
    pre: "pre"
  }, _provideComponents(), props.components);
  return _jsxs(_components.section, {
    children: [_jsx(_components.h1, {
      children: "Введение"
    }), _jsx(_components.p, {
      children: "Проектируя систему, мы занимаемся моделированием, а значит решаем инженерную задачу. Мы строим гипотезу о том, каковы отношения между сущностями в этой системе."
    }), _jsx(_components.p, {
      children: "Однако бизнес-требования не вечны, они могут (и будут) меняться. Хорошо спроектированная система способна пережить эти изменения, отразить их в себе и продолжить функционировать."
    }), _jsx(_components.p, {
      children: "Основная причина, по которой вносить изменения бывает трудно или дорого — когда небольшое изменение в одной части системы вызывает лавину изменений в других частях. Грубо и утрировано: если в программе для изменения цвета кнопки надо поправить 15 модулей, такая система спроектирована плохо."
    }), _jsxs(_components.section, {
      children: [_jsx(_components.h2, {
        children: "Принцип открытости-закрытости"
      }), _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "https://ru.wikipedia.org/wiki/%D0%9F%D1%80%D0%B8%D0%BD%D1%86%D0%B8%D0%BF_%D0%BE%D1%82%D0%BA%D1%80%D1%8B%D1%82%D0%BE%D1%81%D1%82%D0%B8/%D0%B7%D0%B0%D0%BA%D1%80%D1%8B%D1%82%D0%BE%D1%81%D1%82%D0%B8",
          children: "Принцип открытости-закрытости"
        }), " (Open-Closed Principle, OCP) помогает исключить такую проблему. Согласно ему модули должны быть открыты для расширения, но закрыты для изменения."]
      }), _jsx(_components.p, {
        children: "Простыми словами — модули надо проектировать так, чтобы их требовалось менять как можно реже, а расширять функциональность можно было с помощью создания новых сущностей и композиции их со старыми."
      }), _jsx(_components.p, {
        children: "Основная цель принципа — помочь разработать проект, устойчивый к изменениям, срок жизни которых превышает срок существования первой версии проекта."
      }), _jsx(_components.p, {
        children: "Модули, которые удовлетворяют OCP:"
      }), _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.em, {
            children: "открыты для расширения"
          }), " — их функциональность может быть дополнена с помощью других модулей, если изменятся требования;"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.em, {
            children: "закрыты для изменения"
          }), " — расширение функциональности модуля не должно приводить к изменениям в модулях, которые его используют."]
        }), "\n"]
      }), _jsx(_components.p, {
        children: "Конечно, всегда есть изменения, которые невозможно внести, не изменив код какого-то модуля — никакая система не может быть закрыта на 100%. Поэтому при проектировании важен стратегический подход. Необходимо определить, от каких именно изменений и какие именно модули вы хотите закрыть. Это решение следует принимать опираясь на опыт, а также знания предметной области и пользователей системы."
      }), _jsxs(_components.p, {
        children: ["Нарушение принципа открытости-закрытости приводит к ситуациям, когда изменение в одном модуле вынуждает менять другие, связанные с ним. Это в свою очередь ", _jsx(_components.a, {
          href: "/srp",
          children: "нарушает принцип единой ответственности, SRP"
        }), ", потому что весь код, который меняется по какой-то одной причине, должен быть собран в одном модуле. (Разные модули — разные причины для изменения.)"]
      })]
    }), _jsxs(_components.section, {
      children: [_jsx(_components.h2, {
        children: "На примере"
      }), _jsxs(_components.p, {
        children: ["На схеме ниже объект ", _jsx(_components.code, {
          children: "Client"
        }), " непосредственно связан с объектом ", _jsx(_components.code, {
          children: "Server"
        }), ". Если нам вдруг понадобится, чтобы ", _jsx(_components.code, {
          children: "Client"
        }), " мог работать с разными объектами ", _jsx(_components.code, {
          children: "Server"
        }), ", нам придётся поменять его код."]
      }), _jsxs(Figure, {
        children: [_jsx("img", {
          src: "/ocp-violation.svg"
        }), _jsx("figcaption", {
          children: "Структура, нарушающая OCP"
        })]
      }), _jsxs(_components.p, {
        children: ["Чтобы решить эту проблему, необходимо связывать объекты не напрямую, а через абстракции. Если все объекты ", _jsx(_components.code, {
          children: "Server"
        }), " реализуют интерфейс ", _jsx(_components.code, {
          children: "Abstract Server"
        }), ", то нам уже не придётся менять код объекта ", _jsx(_components.code, {
          children: "Client"
        }), " для замены одного объекта ", _jsx(_components.code, {
          children: "Server"
        }), " на другой."]
      }), _jsxs(Figure, {
        children: [_jsx("img", {
          src: "/ocp-resolve.svg"
        }), _jsx("figcaption", {
          children: "Абстракция помогает развязать модули"
        })]
      }), _jsxs(_components.section, {
        children: [_jsx(_components.h3, {
          children: "В коде"
        }), _jsx(_components.p, {
          children: "Если попробовать выразить этот принцип в коде, то самым простым примером будет замена множественных проверок на принадлежность к типу на абстракцию."
        }), _jsx(_components.p, {
          children: "Например, мы пишем сервис рассылки сообщений. Он принимает текст, который надо выслать, и сервис стороннего API для отправки СМС, пушей или электронных писем. Плохо спроектированный сервис мог бы выглядеть так:"
        }), _jsx(_components.pre, {
          children: _jsx(_components.code, {
            className: "language-ts",
            children: "class SmsSender {\n  sendSms(message: MessageText) { /* ... */ }\n}\n\nclass PushSender {\n  sendPush(message: MessageText) { /* ... */ }\n}\n\nclass EmailSender {\n  sendEmail(message: MessageText) { /* ... */ }\n}\n\nclass Notifier {\n  constructor(private api: SmsSender | PushSender | EmailSender) {}\n\n  notify(): void {\n    const message = 'Some user notification';\n\n    if (this.api instanceof SmsSender) {\n      this.api.sendSms(message)\n    } else if (this.api instanceof PushSender) {\n      this.api.sendPush(message)\n    } else if (this.api instanceof EmailSender) {\n      this.api.sendEmail(message)\n    }\n  }\n}\n"
          })
        }), _jsxs(_components.p, {
          children: ["Проблема этого кода в том, что при ", _jsx(_components.em, {
            children: "добавлении"
          }), " нового типа стороннего API — голубиной почты — нам придётся ", _jsx(_components.em, {
            children: "менять"
          }), " уже существующий код."]
        }), _jsx(_components.pre, {
          children: _jsx(_components.code, {
            className: "language-ts",
            children: "// ...Предыдущие классы.\n\n// Добавили новый тип стороннего API:\n\nclass DoveSender {\n  sendDove(message: MessageText) { /* ... */ }\n}\n\nclass Notifier {\n  constructor(private api: SmsSender | PushSender | EmailSender | DoveSender) {}\n\n  notify(): void {\n    const message = 'Some user notification';\n\n    if (this.api instanceof SmsSender) {\n      this.api.sendSms(message)\n    } else if (this.api instanceof PushSender) {\n      this.api.sendPush(message)\n    } else if (this.api instanceof EmailSender) {\n      this.api.sendEmail(message)\n    } else if (this.api instanceof DoveSender) { // Последние 3 строчки —\n      this.api.sendDove(message)                 // ...это новый код,\n    }                                            // ...который пришлось добавить.\n  }\n}\n"
          })
        })]
      }), _jsxs(_components.section, {
        children: [_jsx(_components.h3, {
          children: "Вместо этого"
        }), _jsxs(_components.p, {
          children: ["OCP же предлагает не проверять конкретные типы, а использовать абстракцию, которая позволит не менять код класса ", _jsx(_components.code, {
            children: "Notifier"
          }), ". Для этого мы создадим интерфейс ", _jsx(_components.code, {
            children: "Sender"
          }), ", который будут реализовывать классы ", _jsx(_components.code, {
            children: "SmsSender"
          }), ", ", _jsx(_components.code, {
            children: "PushSender"
          }), " и ", _jsx(_components.code, {
            children: "EmailSender"
          }), ":"]
        }), _jsx(_components.pre, {
          children: _jsx(_components.code, {
            className: "language-ts",
            children: "// Интерфейс будет абстракцией, которая описывает контракт,\n// по которому должны работать классы, реализующий этот интерфейс:\n\ninterface Sender {\n  sendMessage(message: MessageText): void\n}\n\nclass SmsSender implements Sender {\n  sendMessage(message: MessageText) {\n    /* То, что раньше было внутри метода `sendSms`. */\n  }\n}\n\nclass PushSender implements Sender {\n  sendMessage(message: MessageText) {\n    /* То, что раньше было внутри метода `sendPush`. */\n  }\n}\n\nclass EmailSender implements Sender {\n  sendMessage(message: MessageText) {\n    /* То, что раньше было внутри метода `sendEmail`. */\n  }\n}\n"
          })
        }), _jsxs(_components.p, {
          children: ["Тогда классу ", _jsx(_components.code, {
            children: "Notifier"
          }), " перестанет быть нужно проверять конкретный тип, и он сможет положиться на контракт, описанный в интерфейсе:"]
        }), _jsx(_components.pre, {
          children: _jsx(_components.code, {
            className: "language-ts",
            children: "class Notifier {\n  constructor(private api: Sender) {}\n\n  notify(): void {\n    const message = 'Some user notification';\n    this.api.sendMessage(message);\n  }\n}\n"
          })
        })]
      }), _jsxs(_components.section, {
        children: [_jsx(_components.h3, {
          children: "В результате"
        }), _jsxs(_components.p, {
          children: ["Теперь при ", _jsx(_components.em, {
            children: "добавлении"
          }), " голубиной почты, нам уже не потребуется ", _jsx(_components.em, {
            children: "менять"
          }), " код класса ", _jsx(_components.code, {
            children: "Notifier"
          }), ", зависящего от интерфейса ", _jsx(_components.code, {
            children: "Sender"
          }), ":"]
        }), _jsx(_components.pre, {
          children: _jsx(_components.code, {
            className: "language-ts",
            children: "class DoveSender implements Sender {\n  sendMessage(message: MessageText) {\n    /* То, что раньше было внутри метода `sendDove`. */\n  }\n}\n\n// Код класса `Notifier` останется тем же.\n"
          })
        }), _jsx(_components.p, {
          children: "Таким образом, вводя адекватную абстракцию мы «расцепляем» модули. Мы делим зоны ответственности между разными частями приложения и уменьшаем количество кода, который нужно изменять при добавлении новой функциональности."
        })]
      })]
    }), _jsxs(_components.section, {
      children: [_jsx(_components.h2, {
        children: "Коротко"
      }), _jsx(_components.p, {
        children: "Принцип открытости-закрытости:"
      }), _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "заставляет проектировать модули так, чтобы они делали только одну вещь и делали её хорошо;"
        }), "\n", _jsx(_components.li, {
          children: "побуждает связывать сущности через абстракции (а не реализацию) там, где могут поменяться бизнес-требования;"
        }), "\n", _jsx(_components.li, {
          children: "обращает внимание проектировщиков на места стыка и взаимодействие сущностей;"
        }), "\n", _jsx(_components.li, {
          children: "позволяет сократить количество кода, который необходимо менять при изменении бизнес-требований;"
        }), "\n", _jsx(_components.li, {
          children: "делает внесение изменений безопасным и относительно дешёвым."
        }), "\n"]
      })]
    }), _jsxs(_components.section, {
      children: [_jsx(_components.h2, {
        children: "Материалы к разделу"
      }), _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://ru.wikipedia.org/wiki/%D0%9F%D1%80%D0%B8%D0%BD%D1%86%D0%B8%D0%BF_%D0%BE%D1%82%D0%BA%D1%80%D1%8B%D1%82%D0%BE%D1%81%D1%82%D0%B8/%D0%B7%D0%B0%D0%BA%D1%80%D1%8B%D1%82%D0%BE%D1%81%D1%82%D0%B8",
            children: "Принцип открытости-закрытости"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://www2.cs.duke.edu/courses/fall07/cps108/papers/ocp.pdf",
            children: "Open-closed principle, Duke computer science"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://medium.com/@_ericelliott/clarifying-my-use-of-the-open-closed-principle-de54f77342c3",
            children: "Clarifying my use of the open/closed principle, Eric Elliott"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://ru.wikipedia.org/wiki/%D0%9D%D0%B0%D1%83%D1%87%D0%BD%D1%8B%D0%B9_%D0%BC%D0%B5%D1%82%D0%BE%D0%B4",
            children: "Научный метод"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://ru.wikibooks.org/wiki/%D0%9E%D1%81%D0%BD%D0%BE%D0%B2%D1%8B_%D0%A2%D0%A0%D0%98%D0%97",
            children: "Основы теории решения изобретательских задач"
          })
        }), "\n"]
      })]
    }), _jsxs(_components.section, {
      children: [_jsx(_components.h2, {
        children: "Вопросы"
      }), _jsx(Quiz, {
        name: "ocp-intro-1"
      }), _jsx(Quiz, {
        name: "ocp-intro-2"
      })]
    })]
  });
}
function MDXContent(props = {}) {
  return _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, props)
  }));
}
export default MDXContent;
